import { ChartBarIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { BarChart } from '../../components/charts';
import { Button, Card, Chip, Column, DataTable, InlineBanner, MetricTile, PageHeader, Skeleton, SkeletonRegion, StateBlock } from '../../components/ui';
import { useRemote } from '../../features/home/useRemote';
import { useActiveLocation } from '../../hooks/useActiveLocation';
import { usePageTitle } from '../../hooks/usePageTitle';
import api from '../../services/api';
import type { PlanUsage } from '../../types/Restaurant';
import { formatMonth, formatMoney, formatRate } from './format';
import type { RestaurantStatements, Statement } from './types';
import { UsageList } from './UsageList';

const PlanSkeleton = () => (
    <SkeletonRegion className="grid gap-4">
        <Skeleton className="h-40 w-full" />
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {Array.from({ length: 4 }, (_, i) => (
                <Skeleton key={i} className="h-24 w-full" />
            ))}
        </div>
        <Skeleton className="h-64 w-full" />
    </SkeletonRegion>
);

/** "Plan & usage": what the venue pays and how much of the plan it uses. Owner, and Admin for the selected restaurant. */
const PlanPage = () => {
    const { t, i18n } = useTranslation('billing');
    const { t: tc } = useTranslation();
    usePageTitle(t('plan.title'));
    const { restaurantId } = useActiveLocation();
    const lang = i18n.language;

    const statements = useRemote(restaurantId ? `statements:${restaurantId}` : null, () =>
        api.get<RestaurantStatements>(`/restaurants/${restaurantId}/statements`).then((r) => r.data)
    );
    const usage = useRemote(restaurantId ? `usage:${restaurantId}` : null, () =>
        api.get<PlanUsage>(`/restaurants/${restaurantId}/usage`).then((r) => r.data)
    );

    const header = <PageHeader title={t('plan.title')} meta={statements.data?.restaurantName} />;

    if (!restaurantId || statements.state === 'loading') {
        return (
            <div>
                {header}
                <PlanSkeleton />
            </div>
        );
    }
    const data = statements.data;
    if (statements.state === 'error' || !data) {
        return (
            <div>
                {header}
                <Card padding="none">
                    <StateBlock
                        variant="error"
                        title={t('plan.loadFailed')}
                        body={tc('ui.checkConnection')}
                        action={<Button onClick={statements.reload}>{tc('ui.retry')}</Button>}
                    />
                </Card>
            </div>
        );
    }

    const { current, previous } = data;
    const money = (amount: number, currency: string) => formatMoney(amount, currency, lang);
    const rate = formatRate(data.feeRate, lang);
    const hasOrders = [current, ...previous].some((s) => s.completedOrders > 0);
    const trialEnd = data.trialEndsAt ? new Date(data.trialEndsAt) : null;
    const trialDate = trialEnd?.toLocaleDateString(lang, { dateStyle: 'long', timeZone: 'UTC' });
    const monthName = (s: Statement) => formatMonth(s.month, lang);

    const columns: Column<Statement>[] = [
        { id: 'month', header: t('previous.month'), priority: 'primary', cell: (s) => <span className="font-semibold text-strong">{monthName(s)}</span> },
        { id: 'orders', header: t('previous.orders'), priority: 'meta', align: 'end', cell: (s) => s.completedOrders },
        { id: 'value', header: t('previous.value'), priority: 'meta', align: 'end', cell: (s) => money(s.completedValue, s.currency) },
        { id: 'fee', header: t('previous.fee'), priority: 'meta', align: 'end', cell: (s) => `${money(s.fee, s.currency)} (${formatRate(s.feeRate, lang)})` },
        { id: 'plan', header: t('previous.plan'), priority: 'meta', align: 'end', cell: (s) => money(s.planPrice, s.planPriceCurrency) },
        { id: 'total', header: t('previous.total'), priority: 'meta', align: 'end', cell: (s) => (s.total === null ? '–' : money(s.total, s.planPriceCurrency)) },
    ];

    return (
        <div className="grid gap-6">
            {header}
            <Card as="section" padding="lg">
                <h2 className="text-title text-strong">{t('plan.yourPlan')}</h2>
                <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 text-small text-strong">
                    <Chip variant="info" size="lg">
                        {tc(`plans.${data.plan}`)}
                    </Chip>
                    <span>{t('plan.price', { price: money(current.planPrice, current.planPriceCurrency) })}</span>
                    {trialEnd && trialDate && (
                        <span className="text-muted">{trialEnd.getTime() < Date.now() ? t('plan.trialEnded', { date: trialDate }) : t('plan.trial', { date: trialDate })}</span>
                    )}
                </div>
                <h3 className="mb-2 mt-5 text-small font-medium text-strong">{t('usage.title')}</h3>
                {usage.state === 'loading' && (
                    <SkeletonRegion className="grid gap-3">
                        <Skeleton className="h-5 w-full" />
                        <Skeleton className="h-5 w-full" />
                        <Skeleton className="h-5 w-full" />
                    </SkeletonRegion>
                )}
                {usage.state === 'error' && (
                    <InlineBanner tone="danger" action={{ label: tc('ui.retry'), onClick: usage.reload }}>
                        {t('usage.failed')}
                    </InlineBanner>
                )}
                {usage.data && <UsageList usage={usage.data} />}
                <p className="mt-4 text-caption text-muted">{t('usage.upgrade')}</p>
            </Card>

            <section aria-labelledby="month-so-far">
                <h2 id="month-so-far" className="mb-3 text-title text-strong">
                    {t('month.title', { month: monthName(current) })}
                </h2>
                <div className="grid grid-cols-1 gap-3 min-[420px]:grid-cols-2 2xl:grid-cols-4">
                    <MetricTile
                        label={t('month.value')}
                        value={<span className="text-display">{money(current.completedValue, current.currency)}</span>}
                        delta={t('month.orders', { count: current.completedOrders })}
                    />
                    <MetricTile label={t('month.fee')} value={<span className="text-display">{money(current.fee, current.currency)}</span>} delta={t('month.rate', { rate })} />
                    <MetricTile label={t('month.plan')} value={<span className="text-display">{money(current.planPrice, current.planPriceCurrency)}</span>} delta={tc(`plans.${current.plan}`)} />
                    <MetricTile
                        label={t('month.total')}
                        value={<span className="text-display">{current.total === null ? '–' : money(current.total, current.planPriceCurrency)}</span>}
                        delta={current.total === null ? t('month.mixedCurrency') : t('month.estimate')}
                    />
                </div>
            </section>

            <section aria-labelledby="previous-months">
                <h2 id="previous-months" className="mb-3 text-title text-strong">
                    {t('previous.title')}
                </h2>
                {hasOrders && previous.length > 0 ? (
                    <div className="grid gap-4">
                        <Card>
                            <BarChart
                                caption={t('previous.chart')}
                                labelHeading={t('previous.month')}
                                labels={[...previous].reverse().map((s) => formatMonth(s.month, lang, 'short'))}
                                series={[{ key: 'value', label: t('previous.value'), values: [...previous].reverse().map((s) => s.completedValue) }]}
                                formatValue={(n) => money(n, current.currency)}
                            />
                        </Card>
                        <DataTable
                            rows={previous}
                            columns={columns}
                            getRowId={(s) => s.month}
                            state="ready"
                            caption={t('previous.title')}
                            pageSize={12}
                            tableFrom="lg"
                            empty={{ icon: ChartBarIcon, title: t('previous.empty') }}
                        />
                    </div>
                ) : (
                    <Card padding="none">
                        <StateBlock variant="empty" icon={ChartBarIcon} title={t('previous.empty')} body={t('previous.emptyBody')} />
                    </Card>
                )}
            </section>

            <Card as="section" padding="lg">
                <h2 className="text-title text-strong">{t('explain.title')}</h2>
                <ul className="mt-3 grid list-disc gap-2 pl-5 text-small text-body">
                    <li>{t('explain.fee', { rate })}</li>
                    <li>{t('explain.cancelled')}</li>
                    <li>{t('explain.zone', { zone: data.timezone })}</li>
                    <li>{t('explain.invoice')}</li>
                    <li>{t('explain.payments')}</li>
                </ul>
            </Card>
        </div>
    );
};

export default PlanPage;
